// Helpers for parsing the response shape from the
// `click-to-call-gateway` Supabase Edge Function and for guarding
// against rapid double-taps.

const PROVIDER_LABELS = {
  sarv: 'Sarv',
  smartflo: 'Tata SmartFlo',
  tata: 'Tata SmartFlo',
  'tata-smartflo': 'Tata SmartFlo',
};

export function providerLabel(name) {
  if (!name) return 'Unknown';
  const k = String(name).toLowerCase();
  return PROVIDER_LABELS[k] || name;
}

/**
 * Parse the raw service response from TelecallerService.sarvClickToCall().
 *
 * Returns:
 *   {
 *     ok: boolean,
 *     provider: 'sarv' | 'smartflo' | string | null,   // who actually placed the call
 *     providerLabel: 'Sarv' | 'Tata SmartFlo' | ...,
 *     status: 'success' | 'failed' | 'calling',
 *     statusText: string,                              // short status word
 *     reason: string,                                  // human reason if failed
 *     attempts: Array<{provider, ok, reason}>,         // per-provider trace
 *     fellBackFrom: string | null,                     // provider that failed first if fallback was used
 *   }
 */
export function parseCallGatewayResult(serviceResp) {
  const ok = Boolean(serviceResp?.success);
  const data = serviceResp?.data || {};

  const provider = data.provider_used || data.providerUsed || null;
  const attemptsRaw = Array.isArray(data.attempts) ? data.attempts : [];
  const attempts = attemptsRaw.map((a) => ({
    provider: a?.provider || 'unknown',
    providerLabel: providerLabel(a?.provider),
    ok: Boolean(a?.ok),
    reason: a?.reason || (a?.ok ? '' : 'failed'),
  }));

  // Detect if fallback actually kicked in (i.e. an earlier provider failed)
  const failedProvidersBeforeWin = attempts
    .filter((a) => !a.ok)
    .map((a) => a.providerLabel);
  const fellBackFrom = ok && failedProvidersBeforeWin.length > 0 ? failedProvidersBeforeWin[0] : null;

  if (ok) {
    return {
      ok: true,
      provider,
      providerLabel: providerLabel(provider),
      status: 'success',
      statusText: 'success',
      reason: '',
      attempts,
      fellBackFrom,
    };
  }

  // Failure path. Try hard to surface the most useful reason.
  const lastErr = data.last_error || data.lastError || data?.sarv || {};
  const reason =
    serviceResp?.error ||
    lastErr?._gateway_failure_reason ||
    lastErr?.message ||
    lastErr?.Message ||
    lastErr?.error ||
    data?.error ||
    (attempts.length > 0 ? attempts[attempts.length - 1].reason : '') ||
    'Click-to-call failed';

  // If only one provider was tried, surface its label so user knows what failed.
  const triedProviders = attempts.map((a) => a.providerLabel);
  const providerForLabel = provider || triedProviders[triedProviders.length - 1] || null;

  return {
    ok: false,
    provider: providerForLabel,
    providerLabel: providerLabel(providerForLabel),
    status: 'failed',
    statusText: 'failed',
    reason: String(reason),
    attempts,
    fellBackFrom: null,
  };
}

/**
 * Guard: returns true if the same lead was tapped within `cooldownMs` and
 * the call should be ignored. Also returns true if a previous call is still
 * in flight ("calling" phase).
 *
 * Pass the current `callStateByLeadId[leadId]` map entry.
 */
export function shouldBlockTap(currentState, cooldownMs = 4000) {
  if (!currentState) return false;
  if (currentState.status === 'calling') return true;
  if (currentState.lockUntilMs && Date.now() < currentState.lockUntilMs) return true;
  return false;
}

export function makeCoolDownState(prevState, cooldownMs = 4000) {
  return {
    ...(prevState || {}),
    lockUntilMs: Date.now() + cooldownMs,
  };
}
